import { nextTick, ref } from 'vue'
import { debugConsoleError } from '@/modules/chat-matrix-fast/utils/chatDebugLog'
import { clearMessageDraft, getMessageDraft } from '@/modules/chat-matrix-fast/utils/messageDrafts'
import { useMessageDraftSync } from '@/modules/chat-matrix-fast/composables/useMessageDraftSync'
import {
  type MessageWithReply,
  parseQuoteFromContent,
  useChatStore,
  useChatStoreCore,
} from '@/views/apps/chat-matrix-fast/chatModules'

export interface IEditingMessageState {
  id: number | string
  message: MessageWithReply
  originalText: string
}

export interface IReplyToState {
  id: number | string
  message: MessageWithReply
  senderId: number | string
  senderName: string
}

export interface IUseChatMessageInputParams {
  onAfterSend?: () => void
}

export function useChatMessageInput(params: IUseChatMessageInputParams = {}) {
  const store = useChatStore()
  const chatStoreCore = useChatStoreCore()

  const msg = ref('')
  const editingMessage = ref<IEditingMessageState | null>(null)
  const replyTo = ref<IReplyToState | null>(null)
  const isSending = ref(false)

  const getActiveChatId = () => store.activeChat?.chat?.id

  useMessageDraftSync({
    msg,
    getChatId: getActiveChatId,
    isEditing: () => Boolean(editingMessage.value),
    onLeaveChat: () => {
      editingMessage.value = null
      replyTo.value = null
    },
  })

  const onMessageKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void sendMessage()
    }
  }

  const handleStartEdit = (
    messageId: string | number,
    msgData: MessageWithReply,
  ) => {
    const rawText = (msgData.message ?? msgData.content) || ''
    const { remainingText } = parseQuoteFromContent(rawText)
    const originalText = remainingText || rawText

    editingMessage.value = { id: messageId, message: msgData, originalText }
    replyTo.value = null
    msg.value = originalText
  }

  const cancelEdit = () => {
    editingMessage.value = null
    msg.value = getMessageDraft(getActiveChatId())
  }

  const sendMessage = async (mentionUserIds?: string[]) => {
    if (!store.activeChat || isSending.value) return

    if (editingMessage.value) {
      const content = msg.value?.trim()
      if (!content) return
      const chatId = store.activeChat.chat?.id
      if (!chatId) return
      const msgId = editingMessage.value.id
      if (msgId == null || msgId === '') return

      isSending.value = true
      const editState = editingMessage.value
      editingMessage.value = null
      msg.value = getMessageDraft(chatId)

      try {
        await chatStoreCore.updateMessage(
          chatId,
          msgId,
          content,
          editState.message?.matrix_event_id,
        )
        nextTick(() => params.onAfterSend?.())
      } catch (error) {
        msg.value = content
        editingMessage.value = editState
        debugConsoleError(error)
      } finally {
        isSending.value = false
      }

      return
    }

    if (!msg.value?.trim()) return

    isSending.value = true
    const content = msg.value.trim()
    const replyToId = replyTo.value?.id
    const replyToMessage = replyTo.value?.message
    const chatId = getActiveChatId()
    msg.value = ''
    replyTo.value = null
    clearMessageDraft(chatId)

    try {
      await store.sendMsg(content, replyToId, replyToMessage, mentionUserIds)
      nextTick(() => params.onAfterSend?.())
    } catch (error) {
      msg.value = content
      debugConsoleError(error)
    } finally {
      isSending.value = false
    }
  }

  const handleReply = (message: MessageWithReply, senderId: number | string) => {
    const senderName
      = (message as { created_by?: { full_name?: string; user_name?: string } }).created_by?.full_name
      || (message as { created_by?: { user_name?: string } }).created_by?.user_name
      || (message as { author_name?: string }).author_name
      || (message as { name?: string }).name
      || store.activeChat?.contact?.fullName
      || 'Неизвестный'

    const matrixEventId = (message as { matrix_event_id?: string }).matrix_event_id
    replyTo.value = {
      id: matrixEventId
        || (message as { id?: number | string }).id
        || `${senderId}-${(message as { time?: string | number }).time}`,
      message,
      senderId,
      senderName,
    }
  }

  /** Выйти из режима ответа без отправки. */
  const cancelReply = () => {
    replyTo.value = null
  }

  return {
    msg,
    editingMessage,
    replyTo,
    isSending,
    sendMessage,
    cancelEdit,
    cancelReply,
    handleReply,
    handleStartEdit,
    onMessageKeydown,
  }
}
